import { useState, useEffect, type RefObject } from 'react';
import { useLocation } from '@tanstack/react-router';

export interface TocEntry {
  id: string;
  text: string;
  level: 2 | 3;
}

interface UseTableOfContentsReturn {
  entries: TocEntry[];
  activeId: string;
}

export function useTableOfContents(
  scrollContainerRef: RefObject<HTMLElement | null>,
): UseTableOfContentsReturn {
  const [entries, setEntries] = useState<TocEntry[]>([]);
  const [activeId, setActiveId] = useState('');
  const location = useLocation();

  // Extract headings from DOM when route changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Small delay to let the page render
    const timer = setTimeout(() => {
      const headings = container.querySelectorAll<HTMLElement>('h2[id], h3[id]');
      const newEntries: TocEntry[] = [];

      headings.forEach((heading) => {
        const level = heading.tagName === 'H2' ? 2 : 3;
        newEntries.push({
          id: heading.id,
          text: heading.textContent || '',
          level: level as 2 | 3,
        });
      });

      setEntries(newEntries);

      // Set initial active to first heading
      if (newEntries.length > 0) {
        setActiveId(newEntries[0].id);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname, scrollContainerRef]);

  // Scroll-spy with IntersectionObserver
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || entries.length === 0) return;

    const observer = new IntersectionObserver(
      (observerEntries) => {
        // Find the first intersecting heading
        for (const entry of observerEntries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            break;
          }
        }
      },
      {
        root: container,
        rootMargin: '0px 0px -70% 0px',
        threshold: 0,
      },
    );

    const headings = container.querySelectorAll<HTMLElement>('h2[id], h3[id]');
    headings.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, [entries, scrollContainerRef]);

  return { entries, activeId };
}
